import React from "react";
import { Box, CircularProgress, DialogActions, FormHelperText, IconButton, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import enzaLocale from "date-fns/locale/en-ZA";
import FeatherIcon from "feather-icons-react";
import { Formik } from "formik";
import moment from "moment";
import { DraggableDialog } from "../../consts.js";
import { CAMPAIGN_DIRECTION, CAMPAIGN_TYPE_OPTIONS } from "../../helpers/constant.js";
import CreateCampaignStepper from "./create-campaign-header-stepper.jsx";
import { AddCampaignSchema } from "./validator.js";
import AppSelect from "../../components/common-form/select-input/index.jsx";
import { useSnackbar } from "../../components/toast/index.jsx";


const CreateCampaign = ({ open, onClose, companyData, allCampaigns }) => {
    const showSnackbar = useSnackbar();
    // auto populate date as today and 10 days from today
    const days = +(process.env.REACT_APP_CREATE_CAMPAIGN_DEFAULT_DAYS || 10)
    const today = new Date();
    const endDateCustom = new Date(new Date().setDate(today.getDate() + days));

    const initialValues = {
        companyName: companyData.name,
        campaignName: "",
        type: "",
        direction: "",
        startDate: moment(today).format("YYYY-MM-DD"),
        endDate: moment(endDateCustom).format("YYYY-MM-DD")
    }

    /**
     * Submit Method to create a campaign
     */
    const submitForm = async (values, { setSubmitting }) => {
        const campaignsExist = allCampaigns.some(s => s.name.trim().toLowerCase() === values.campaignName.trim().toLowerCase());
        if(campaignsExist) {
            showSnackbar(`Campaign "${values.campaignName}" already exists.`, "error");
            setSubmitting(false);
            return;
        }
        setSubmitting(false);
        onClose({
            companyId: companyData.id,
            companyName: companyData.name,
            campaignName: values.campaignName.trim(),
            type: values.type,
            direction: values.direction,
            startDate: values.startDate,
            endDate: values.endDate,
        });
    }

    const handleClose = (event, reason) => {
        // Prevent closing the dialog on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        event.stopPropagation();
        onClose(null);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
            disableEscapeKeyDown={true}
            fullWidth={true}
            maxWidth="sm"
            PaperComponent={DraggableDialog}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle
                id="draggable-dialog-title"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={1}
            >
                New Campaign
                <IconButton
                    className="draggable-cancel"
                    size="small"
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        color: theme.palette.grey[400],
                        marginRight: "-.5rem",
                    })}
                >
                    <FeatherIcon icon="x" />
                </IconButton>
            </DialogTitle>
            <Formik
                enableReinitialize
                validationSchema={AddCampaignSchema}
                initialValues={initialValues}
                onSubmit={(values, actions) => {
                    submitForm(values, actions);
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    values,
                    errors,
                    touched,
                    isSubmitting,
                }) => (
                    <Box
                        component="form"
                        display="flex"
                        flexDirection="column"
                        flexGrow={1}
                        overflow="auto"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <DialogContent dividers sx={{ paddingY: 3 }}>
                            <CreateCampaignStepper current={1} />
                            <TextField
                                id="outlined-basic"
                                variant="outlined"
                                fullWidth 
                                sx={{ marginTop: 3}}
                                label="Entity"
                                required
                                name={"companyName"}
                                value={values.companyName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.companyName && !!errors.companyName}
                                helperText={touched.companyName ? errors.companyName : ''}
                                disabled={true}
                            />
                            <TextField
                                id="outlined-basic"
                                variant="outlined"
                                style={{ width: "100%", marginTop: 20 }}
                                label="Campaign Name"
                                required
                                name={"campaignName"}
                                value={values.campaignName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.campaignName && !!errors.campaignName}
                                helperText={touched.campaignName ? errors.campaignName : ''}
                            />
                            <AppSelect
                                label="Type"
                                name={"type"}
                                options={CAMPAIGN_TYPE_OPTIONS}
                                value={values.type}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.type}
                                error={errors.type}
                                disabled={isSubmitting}
                                validationSchema={AddCampaignSchema}
                                MenuProps={{ width: "100%" }}
                                sx={{ marginTop: 3 }}
                            />
                            <AppSelect
                                label="Call Direction"
                                name={"direction"}
                                options={CAMPAIGN_DIRECTION}
                                value={values.direction}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.direction}
                                error={errors.direction}
                                disabled={isSubmitting}
                                validationSchema={AddCampaignSchema}
                                MenuProps={{ width: "100%" }}
                                sx={{ marginTop: 3 }}
                            />
                            <Stack flexDirection="row" gap={{ xs: 1, sm: 2.5 }} sx={{ marginTop: 2.5, width: "100%" }}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDateFns}
                                    locale={enzaLocale}
                                >
                                    <div style={{ width: "100%" }}>
                                        <DatePicker
                                            label="Start Date"
                                            name={"startDate"}
                                            slotProps={{ textField: { required: true } }}
                                            value={moment(values.startDate).toDate()}
                                            onChange={(date) => {
                                                const startDate = moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : null;
                                                setFieldValue('startDate', startDate)
                                            }}
                                            onClose={() => setFieldTouched('startDate', true)}
                                            onError={() => setFieldTouched('startDate', true)}
                                            error={touched.startDate && !!errors.startDate}
                                            renderInput={(params) => <TextField {...params} />}
                                            sx={{ width: "100%" }}
                                        />
                                        {(touched.startDate && !!errors.startDate) ? (
                                            <FormHelperText error={true}>{errors.startDate}</FormHelperText>
                                        ) : ''}
                                    </div>
                                    <div style={{ width: "100%" }}>
                                        <DatePicker
                                            label="End Date"
                                            name={"endDate"}
                                            slotProps={{ textField: { required: true } }}
                                            minDate={values.startDate ? moment(values.startDate).toDate() : null}
                                            value={moment(values.endDate).toDate()}
                                            onChange={(date) => {
                                                const endDate = moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : null;
                                                setFieldValue('endDate', endDate)
                                            }}
                                            onClose={() => setFieldTouched('endDate', true)}
                                            onError={() => setFieldTouched('endDate', true)}
                                            error={touched.endDate && !!errors.endDate}
                                            renderInput={(params) => <TextField {...params} />}
                                            sx={{ width: "100%" }}
                                        />
                                        {(touched.endDate && !!errors.endDate) ? (
                                            <FormHelperText error={true}>{errors.endDate}</FormHelperText>
                                        ) : ''}
                                    </div>
                                </LocalizationProvider>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Next"}
                            </Button>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
}

export default CreateCampaign;